import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { SignInForm } from '../components/auth/SignInForm';
import { useJWTAuth } from '../contexts/useJWTAuth';
import { analytics } from '../services/analytics/posthog';

const SignInPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useJWTAuth();

  const sessionExpired =
    location.state?.sessionExpired || sessionStorage.getItem('auth_session_expired') === 'true';

  useEffect(() => {
    analytics.pageViewed('signin');
  }, []);

  // Redirect to dashboard (or saved return URL) if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const savedReturnUrl = sessionStorage.getItem('auth_return_url');
      sessionStorage.removeItem('auth_return_url');
      sessionStorage.removeItem('auth_session_expired');
      navigate(savedReturnUrl || '/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleSuccess = () => {
    const savedReturnUrl = sessionStorage.getItem('auth_return_url');
    sessionStorage.removeItem('auth_return_url');
    sessionStorage.removeItem('auth_session_expired');
    navigate(savedReturnUrl || '/dashboard', { state: { isFirstVisit: false }, replace: true });
  };

  const handleSwitchToSignUp = () => {
    navigate('/signup');
  };

  // Don't render if user is already authenticated (while redirect happens)
  if (!isLoading && isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar with auth button hidden — user is already signing in */}
      <Navbar showAuthButton={false} />

      {/* Main Content */}
      <main className="flex-1 px-6 lg:px-8 py-12 pt-24">
        <div className="max-w-7xl mx-auto">
          {/* Session Expired Banner */}
          {sessionExpired && (
            <div
              className="mb-8 rounded-lg px-4 py-3 flex items-center gap-3"
              role="alert"
              style={{
                backgroundColor: 'rgba(217, 119, 6, 0.1)',
                border: '1px solid rgba(217, 119, 6, 0.3)',
              }}
            >
              <Clock className="w-5 h-5 shrink-0" style={{ color: 'rgb(217, 119, 6)' }} />
              <p className="text-sm" style={{ color: 'rgb(217, 119, 6)' }}>
                <span className="font-semibold">Session expired</span> — Your session has expired.
                Please sign in again to continue.
              </p>
            </div>
          )}

          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Marketing Content */}
            <div className="hidden lg:block space-y-8">
              <div className="space-y-4">
                <h1
                  className="text-4xl font-bold leading-tight"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Sign in to your account
                </h1>
                <p
                  className="text-lg leading-relaxed"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Access your dashboard to continue your code analysis.
                </p>
              </div>
            </div>

            {/* Right Side - Auth Card */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <div
                className="bg-white dark:bg-gray-800 rounded-xl p-8"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-default)',
                }}
              >
                {/* Header */}
                <div className="text-center mb-8">
                  <h2
                    className="text-2xl font-semibold mb-2"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    Sign in
                  </h2>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    Enter your credentials to continue
                  </p>
                </div>

                {/* Sign In Form */}
                <SignInForm onSuccess={handleSuccess} onSwitchToSignUp={handleSwitchToSignUp} />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SignInPage;
